import { motion, AnimatePresence } from 'framer-motion';
import { X, BarChart3, TrendingUp, Eye, CheckCircle } from 'lucide-react';

interface EvaluationPostureModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function EvaluationPostureModal({ isOpen, onClose }: EvaluationPostureModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gradient-to-br from-slate-900 via-orange-900 to-slate-900 rounded-2xl max-w-5xl w-full my-8 border border-orange-500/30 shadow-2xl shadow-orange-500/20"
          >
            {/* Header */}
            <div className="relative p-8 border-b border-orange-500/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl">
                    <BarChart3 className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-400">
                      Évaluation de Posture Cybersécurité
                    </h2>
                    <p className="text-gray-400 mt-1 text-lg">
                      Analyse complète avec visualisations avancées - CyberOps
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 space-y-8 max-h-[calc(100vh-300px)] overflow-y-auto">
              
              <div className="bg-gradient-to-r from-orange-600/20 via-red-600/20 to-orange-600/20 rounded-xl p-8 border-2 border-orange-500/50">
                <h3 className="text-2xl font-bold text-center text-white mb-4">
                  L'Évaluation : La Base de Tout
                </h3>
                <p className="text-gray-300 text-center text-lg">
                  L'évaluation de posture, c'est la base. Nous devons voir clairement où vous en êtes. 
                  Les <strong className="text-white">graphiques spider</strong>, les <strong className="text-white">visualisations</strong>, 
                  tout ça permet de communiquer efficacement. C'est visuel, c'est clair, c'est professionnel.
                </p>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-xl p-6 border border-orange-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Eye className="w-7 h-7 text-orange-400" />
                    Ce Que Nous Évaluons
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-orange-400 font-bold mb-2">Selon NIST 2.0</h5>
                      <ul className="space-y-1 text-sm text-gray-300">
                        <li>• GOVERN (Gouvernance)</li>
                        <li>• IDENTIFY (Identifier)</li>
                        <li>• PROTECT (Protéger)</li>
                        <li>• DETECT (Détecter)</li>
                        <li>• RESPOND (Répondre)</li>
                        <li>• RECOVER (Récupérer)</li>
                      </ul>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-red-400 font-bold mb-2">Aspects Techniques</h5>
                      <ul className="space-y-1 text-sm text-gray-300">
                        <li>• Infrastructure et configurations</li>
                        <li>• Microsoft 365 / Office 365</li>
                        <li>• Outils de sécurité</li>
                        <li>• Processus et procédures</li>
                        <li>• Conformité (Loi 25, etc.)</li>
                        <li>• Formation et sensibilisation</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-xl p-6 border border-red-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <BarChart3 className="w-7 h-7 text-red-400" />
                    Visualisations avec CyberOps
                  </h4>
                  <p className="text-gray-300 mb-4">
                    CyberOps transforme les résultats de l'évaluation en <strong className="text-white">visualisations claires</strong> :
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Graphiques spider</strong> pour analyse multidimensionnelle</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Comparaisons historiques</strong> pour voir l'évolution</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Identification des forces et faiblesses</strong> de manière visuelle</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Recommandations actionnables</strong> basées sur l'évaluation</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-xl p-6 border border-yellow-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <TrendingUp className="w-7 h-7 text-yellow-400" />
                    Résultats et Prochaines Étapes
                  </h4>
                  <p className="text-gray-300">
                    Une bonne évaluation de posture, c'est un <strong className="text-white">outil puissant</strong>. 
                    Nous montrons clairement où vous en êtes, où vous devriez être, et comment nous allons vous y amener. 
                    C'est concret, c'est rassurant, c'est professionnel.
                  </p>
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="p-6 border-t border-orange-500/30 bg-slate-900/50">
              <div className="flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-200 font-semibold shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40 hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

